import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { Language } from "@/data/model/Enums";
import type { IIdenticalText } from "@/data/model/Shared";
import { exceptionTypes } from "@/data/util/Config";

import { logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";
import { DomUtils } from "@/portal/helpers/DomUtils";
import { UiNotification } from "@/portal/ui/Notification";

// the order the languages are listed in the warning
const languageOrder = [Language.English, Language.German, Language.French, Language.Italian];

export class BeforeSaveValidation {
    // the validation fails with identical_entry_found_exception when another entry of the location already uses one of
    // the entered texts - the user is asked then, and this resolves whether the save goes on. Any other failure lets it
    // go on too: the check only warns, so it must not keep the user from saving
    public static confirm(validation: Promise<void>, $nameField: JQuery): Promise<boolean> {
        return validation
            .then(() => true)
            .catch((ex: DaoError) => {
                if (ex?.additionalData?.reason !== exceptionTypes.identicalEntryFoundException) {
                    logError(ex);
                    return true;
                }

                return BeforeSaveValidation.ask(ex.additionalData.identicalTexts, $nameField);
            });
    }

    private static ask(identicalTexts: Array<IIdenticalText>, $nameField: JQuery): Promise<boolean> {
        const texts = identicalTexts
            .map((d) => '"' + d.text + '"')
            .filter((t, i, all) => all.indexOf(t) === i)
            .join(", ");
        const languages = languageOrder
            .filter((l) => identicalTexts.some((d) => d.language === l))
            .map((l) => i18n("identicalEntry.languages." + Language[l]))
            .join(", ");

        return new Promise<boolean>((resolve) => {
            UiNotification.confirmWithOptions({
                title: i18n("identicalEntry.title"),
                message: i18n("identicalEntry.message", { texts, languages }),
                confirmBtn: i18n("identicalEntry.saveAnyway"),
                cancelBtn: i18n("identicalEntry.editText"),
                confirmButtonClass: "btn btn-success",
                cancelButtonClass: "btn btn-warning",
                confirmed: () => resolve(true),
                cancelled: () => {
                    DomUtils.scrollTo($nameField);
                    resolve(false);
                },
                dismissed: () => resolve(false),
            });
        });
    }
}
